import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "@/design-system/native";
import { Download, X } from "lucide-react";
import styles from "./ImportModal.module.scss";
import {
  buildSampleData,
  buildSchemaFromColumns,
  buildSchemaFromFields,
  ensureRowId,
  makeFileName,
  normalizeImportedRow,
} from "../../utils/importing";

const ACCEPT_EXT = ".xlsx";

const readFileAsArrayBuffer = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(e);
    reader.readAsArrayBuffer(file);
  });

const parseFile = async (file) => {
  // xlsx (~874kB) lazy import — yalnız dosya import edilince yüklenir.
  const XLSX = await import("xlsx");
  const buffer = await readFileAsArrayBuffer(file);
  const workbook = XLSX.read(buffer, { type: "array" });
  const sheetName = workbook.SheetNames?.[0];
  if (!sheetName) return [];
  const sheet = workbook.Sheets[sheetName];
  return XLSX.utils.sheet_to_json(sheet, { defval: "" });
};

export default function ImportModal({ isOpen, toggle, config = {} }) {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const {
    title = "Import",
    columns,
    fields,
    schema: schemaProp,
    sampleData: sampleDataProp,
    sampleFileName,
    sampleSheetName = "Sheet1",
    multiple = false,
    setRows,
    onImport,
    idKey = "id",
  } = config || {};

  const schema = useMemo(() => {
    if (schemaProp?.length) return schemaProp;
    if (columns?.length) return buildSchemaFromColumns(columns);
    if (fields?.length) return buildSchemaFromFields(fields);
    return [];
  }, [schemaProp, columns, fields]);

  const sampleData = useMemo(() => {
    if (sampleDataProp?.length) return sampleDataProp;
    return buildSampleData(schema);
  }, [sampleDataProp, schema]);

  useEffect(() => {
    if (isOpen) {
      setFiles([]);
      setError("");
      setDragging(false);
      setBusy(false);
    }
  }, [isOpen]);

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    if (!incoming.length) return;
    const onlyXlsx = incoming.filter((f) =>
      String(f.name || "").toLowerCase().endsWith(".xlsx")
    );
    if (!onlyXlsx.length) {
      setError("Please upload an Excel file (.xlsx).");
      return;
    }
    setError("");
    setFiles((prev) =>
      multiple ? [...prev, ...onlyXlsx] : onlyXlsx.slice(0, 1)
    );
  };

  const handleDownloadSample = async () => {
    const XLSX = await import("xlsx");
    const data = sampleData?.length ? sampleData : buildSampleData(schema);
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sampleSheetName);
    XLSX.writeFile(wb, sampleFileName || makeFileName(title));
  };

  const handleNext = async () => {
    if (!files.length || busy) return;
    setBusy(true);
    setError("");
    try {
      const parsed = [];
      for (const file of files) {
        const rows = await parseFile(file);
        parsed.push(...rows);
      }

      const nonEmpty = parsed.filter((row) =>
        Object.values(row || {}).some(
          (value) =>
            value !== null &&
            value !== undefined &&
            String(value).trim() !== ""
        )
      );

      const normalized = nonEmpty
        .map((row) => normalizeImportedRow(row, schema))
        .map((row) => ensureRowId(row, idKey));

      if (setRows) {
        setRows((prev) => [...prev, ...normalized]);
      }
      onImport?.(normalized, parsed);
      toggle?.();
    } catch (err) {
      setError("File could not be processed. Please check the format.");
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    addFiles(e.dataTransfer?.files);
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      backdrop="static"
      keyboard={false}
      centered
    >
      <ModalHeader toggle={toggle}>
        <span className={styles.title}>{title}</span>
      </ModalHeader>
      <ModalBody>
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setDragging(false);
          }}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={`${styles.dropzone}${
            dragging ? ` ${styles["dropzone--dragging"]}` : ""
          }`}
        >
          <div>
            <div>Drag-and-drop documents here</div>
            <div className={styles.hint}>(or click to browse...)</div>
            {files.length > 0 && (
              <div className={styles.selectedCount}>
                {files.length} file{files.length > 1 ? "s" : ""} selected
              </div>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT_EXT}
            multiple={multiple}
            hidden
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </div>

        {!!files.length && (
          <div className={styles.fileList}>
            {files.map((file, idx) => (
              <div key={`${file.name}-${idx}`} className={styles.fileRow}>
                <span className={styles.fileName}>{file.name}</span>
                <Button
                  color="white"
                  className={styles.iconBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    setFiles((prev) => prev.filter((_, i) => i !== idx));
                  }}
                >
                  <X size={14} />
                </Button>
              </div>
            ))}
          </div>
        )}

        {error && <div className={styles.error}>{error}</div>}
      </ModalBody>
      <ModalFooter className={styles.footer}>
        <Button
          color="white"
          className={styles.lightBtn}
          onClick={handleDownloadSample}
        >
          <Download size={16} className={styles.btnIcon} /> Download sample file
        </Button>
        <div className={styles.footerActions}>
          <Button
            color="white"
            className={styles.lightBtn}
            onClick={toggle}
          >
            Close
          </Button>
          <Button color="primary" disabled={!files.length || busy} onClick={handleNext}>
            {busy ? "Processing..." : "Next"}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
}
